import { Box, Button, Dialog, Typography } from '@mui/material';
import {
  couponUnavailableRibbonSrc,
  redemptionBackArrowSrc,
  redemptionTitleRibbonSrc,
} from '../../assets/bazzar';

const ACTION_BUTTON_WIDTH = 306;
const ACTION_BUTTON_HEIGHT = 64;

/** Figma 8736:26367 / 9943:26551 — inner dialog panel background */
export const BAZAAR_DIALOG_INNER_BG = 'linear-gradient(180deg, #3d03a1 1.247%, #22005c 100%)';
export const BAZAAR_DIALOG_INNER_SHADOW = 'inset 0 12px 10px rgba(43, 0, 118, 0.39)';

/** Figma 9943:26541 / 8736:26438–26443 — redemption-title-ribbon.svg artboard */
export const BAZAAR_RIBBON_ARTBOARD_WIDTH = 416.28;
export const BAZAAR_RIBBON_ARTBOARD_HEIGHT = 78.5301;
export const BAZAAR_RIBBON_CENTER_WIDTH = 346;
export const BAZAAR_RIBBON_SIDE_BLEED = (BAZAAR_RIBBON_ARTBOARD_WIDTH - BAZAAR_RIBBON_CENTER_WIDTH) / 2;
/** Figma 10040:6897 — red error ribbon artboard */
export const BAZAAR_ERROR_RIBBON_ARTBOARD_WIDTH = 408.28;
export const BAZAAR_ERROR_RIBBON_ARTBOARD_HEIGHT = 69.53;
export const BAZAAR_DIALOG_WIDTH = 356;

export function BazaarDialogShell({ open, onClose, children, minHeight = 325 }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-container': { overflow: 'visible' },
      }}
      PaperProps={{
        sx: {
          bgcolor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
          m: 2,
        },
      }}
    >
      <Box sx={{ maxWidth: '450px',minWidth: '350px', overflow: 'visible' }}>
        <Box
          sx={{
            borderRadius: '35px',
            border: '1px solid #d6a7ff',
            background: 'linear-gradient(180deg, #8f51fb 0%, #5c1ec8 34.483%)',
            boxShadow: '0 0 16px 6px rgba(183,89,255,0.35)',
            p: '5px',
            overflow: 'visible',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              minHeight,
              borderRadius: '32px',
              overflow: 'visible',
              background: BAZAAR_DIALOG_INNER_BG,
              boxShadow: BAZAAR_DIALOG_INNER_SHADOW,
              pt: `${BAZAAR_RIBBON_ARTBOARD_HEIGHT - 24}px`,
              pb: 3,
              px: 2.5,
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

const RIBBON_TEXT_SHADOW = {
  success: '0 2px 0 #184c00',
  error: '0 2px 0 #4c0000',
};

/** Figma 9943:26541 / 10040:6897 (6898+6899+6900) — dialog title ribbon */
export function BazaarRibbonTitle({ children, tone = 'success', ribbonVariant = 'success' }) {
  const isErrorRibbon = ribbonVariant === 'error';
  const ribbonSrc = isErrorRibbon ? couponUnavailableRibbonSrc : redemptionTitleRibbonSrc;
  const ribbonHeight = isErrorRibbon
    ? BAZAAR_ERROR_RIBBON_ARTBOARD_HEIGHT
    : BAZAAR_RIBBON_ARTBOARD_HEIGHT;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '15px',
        left: '50%',
        zIndex: 2,
        width: '130%',
        height: ribbonHeight,
        transform: 'translateX(-50%)',
        pointerEvents: 'none',
      }}
    >
      <Box
        component="img"
        src={ribbonSrc}
        alt=""
        sx={{
          width: '100%',
          height: '100%',
          display: 'block',
          objectFit: 'fill',
          filter: isErrorRibbon ? 'none' : 'drop-shadow(0 5px 4px rgba(0, 0, 0, 0.22))',
        }}
      />
      <Typography
        sx={{
          position: 'absolute',
          left: '50%',
          top: 13,
          transform: 'translateX(-50%)',
          width: 288,
          maxWidth: '72%',
          textAlign: 'center',
          fontFamily: 'Inter, sans-serif',
          color: '#fff',
          fontWeight: 700,
          fontSize: 20,
          lineHeight: '32px',
          letterSpacing: '0.8px',
          textShadow: RIBBON_TEXT_SHADOW[tone] || RIBBON_TEXT_SHADOW.success,
          pointerEvents: 'auto',
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

/** Figma 10333:54413 / 8736:26425 + 26426 — gradient confirm button with glow */
export function BazaarActionButton({ label, onClick, loading = false, disabled = false }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: ACTION_BUTTON_WIDTH,
        height: ACTION_BUTTON_HEIGHT + 8,
        mx: 'auto',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          height: ACTION_BUTTON_HEIGHT,
          borderRadius: '100px',
          background: 'linear-gradient(256.71deg, #670062 1.55%, #33007d 97.93%)',
          boxShadow: '0 0 20px rgba(252, 0, 241, 0.36)',
        }}
      />
      <Button
        onClick={onClick}
        disabled={loading || disabled}
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 8,
          minHeight: ACTION_BUTTON_HEIGHT,
          borderRadius: '100px',
          border: '0.3px solid #fff',
          background: 'linear-gradient(270deg, #822aff 0%, #ff00f2 100%)',
          fontFamily: 'Inter, sans-serif',
          color: '#fff',
          fontSize: 18,
          fontWeight: 500,
          lineHeight: '22px',
          letterSpacing: 0,
          textTransform: 'uppercase',
          opacity: loading || disabled ? 0.75 : 1,
          '&:hover': {
            background: 'linear-gradient(270deg, #9330ff 0%, #ff1af5 100%)',
          },
        }}
      >
        {loading ? 'Processing...' : label}
      </Button>
    </Box>
  );
}

/** Figma 8736:26371 + 26372 — back link */
export function BazaarBackLink({ onClick, label = 'Back to RapBazaar', sx }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        mt: 2.4,
        mx: 'auto',
        border: 0,
        bgcolor: 'transparent',
        cursor: 'pointer',
        fontFamily: 'Inter, sans-serif',
        color: '#ff00f2',
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '22px',
        letterSpacing: 0,
        textTransform: 'uppercase',
        ...sx,
      }}
    >
      <Box component="img" src={redemptionBackArrowSrc} alt="" sx={{ width: 20, height: 20, display: 'block' }} />
      {label}
    </Box>
  );
}
